/**
 * Weather tab (`ldt-weather`, spec §15):
 *   - Forecast time scrub (Now / +1h / +2h / +3h / +4h)
 *   - Conditions cells (temp/humidity/visibility)
 *   - Wind cells (speed/direction/gust)
 *   - Sensors list (with show-on-map)
 *
 * Data sources:
 *   - LIVE mode: derived from `useWxStore.allStations` (populated by the
 *     MQTT `weather/sensors` topic). v1 ships current-conditions only —
 *     no forecast horizon — so `forecast[0]` is built from the nearest
 *     MSC METAR station and `[1..4]` are null (cells render `—`).
 *   - MOCK mode: REST `GET /api/weather?lat=&lng=` (served by MSW).
 *
 * Spec note §30.10: weather is not in 006/007 yet. When the canonical
 * envelope lands we'll replace `useWeatherData` with a single store-
 * driven path.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { http } from '@/lib/api/http';
import { useWxStore, type WxSensorPoint } from '@/lib/store/wx';
import { config } from '@/lib/config';
import type { WxSensorRecord } from '@/lib/mqtt/schemas/weather';
import { Section, SectionGroup } from '@/components/Section';
import { MetricCell, MetricRow3 } from '@/components/MetricCell';
import { InfoRow } from '@/components/InfoRow';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion } from '@/components/ui/accordion';
import { degToCardinal } from '@/lib/format';
import { MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { droneContextFlags } from '@/lib/droneContextFlags';
import { fitMapToPoints } from '@/lib/map/fitPoints';
import { useUiStore } from '@/lib/store/ui';

interface WxForecastTick {
  tempC: number | null;
  feelsC: number | null;
  humidityPct: number | null;
  pressureHpa: number | null;
  visKm: number | null;
  cloudsPct: number | null;
  description: string | null;
  windSpdKts: number | null;
  windGustKts: number | null;
  windDirDeg: number | null;
}
interface WxStation {
  station_name: string;
  source: 'Alberta511' | 'Netatmo' | 'MSC';
  distKm: number;
  bearingDeg: number;
  location: string;
  windSpdMph: number | null;
  windDirDeg: number | null;
  tempC: number | null;
  humidityPct: number | null;
  pressureHpa: number | null;
  dewPointC: number | null;
  visibility: string | null;
  camera_url: string | null;
}
interface WxResponse {
  metar_station: string | null;
  metar_distance_km: number | null;
  forecast: WxForecastTick[]; // 5 ticks: Now + +1..+4h (live populates [0] only)
  sensors: WxStation[];
}

// Live-mode helpers ────────────────────────────────────────────────
// Radius is the *only* size limit — no slice cap. Capping at the list
// boundary made list-count diverge from map-count when the radius
// caught more than the cap.
const NEARBY_RADIUS_KM = 50;
const KMH_TO_MPH = 0.621371;
const KMH_TO_KTS = 0.539957;
const EMPTY_TICK: WxForecastTick = {
  tempC: null, feelsC: null, humidityPct: null, pressureHpa: null,
  visKm: null, cloudsPct: null, description: null,
  windSpdKts: null, windGustKts: null, windDirDeg: null,
};

function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
function bearingDeg(from: { lat: number; lng: number }, to: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const dLon = toRad(to.lng - from.lng);
  const fromLat = toRad(from.lat);
  const toLat = toRad(to.lat);
  const y = Math.sin(dLon) * Math.cos(toLat);
  const x = Math.cos(fromLat) * Math.sin(toLat) -
    Math.sin(fromLat) * Math.cos(toLat) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function recordToStation(r: WxSensorRecord, distKm: number, brg: number): WxStation {
  return {
    station_name: r.station_name,
    source: r.source,
    distKm,
    bearingDeg: brg,
    location: r.location ?? r.roadway ?? '',
    windSpdMph: r.wind_speed_kmh != null ? r.wind_speed_kmh * KMH_TO_MPH : null,
    windDirDeg: r.wind_dir_deg,
    tempC: r.temp_c,
    humidityPct: r.humidity_pct,
    pressureHpa: r.pressure_hpa,
    dewPointC: r.dew_point_c,
    visibility: r.visibility_m != null ? `${(r.visibility_m / 1000).toFixed(1)} km` : null,
    camera_url: r.camera_url,
  };
}

function buildLiveResponse(lat: number, lng: number, all: WxSensorRecord[]): WxResponse | null {
  if (all.length === 0) return null;
  const here = { lat, lng };
  const ranked = all
    .map((s) => ({ s, d: haversineKm(here, { lat: s.lat, lng: s.lng }) }))
    .filter((r) => r.d <= NEARBY_RADIUS_KM)
    .sort((a, b) => a.d - b.d);

  const sensors = ranked.map((r) =>
    recordToStation(r.s, r.d, bearingDeg(here, { lat: r.s.lat, lng: r.s.lng })),
  );

  const nearestMsc = ranked.find((r) => r.s.source === 'MSC');
  let forecast: WxForecastTick[];
  if (nearestMsc) {
    const ms = nearestMsc.s;
    const now: WxForecastTick = {
      tempC: ms.temp_c,
      feelsC: null,
      humidityPct: ms.humidity_pct,
      pressureHpa: ms.pressure_hpa,
      visKm: ms.visibility_m != null ? ms.visibility_m / 1000 : null,
      cloudsPct: null,
      description: null,
      windSpdKts: ms.wind_speed_kmh != null ? ms.wind_speed_kmh * KMH_TO_KTS : null,
      windGustKts: null,
      windDirDeg: ms.wind_dir_deg,
    };
    forecast = [now, EMPTY_TICK, EMPTY_TICK, EMPTY_TICK, EMPTY_TICK];
  } else {
    forecast = [EMPTY_TICK, EMPTY_TICK, EMPTY_TICK, EMPTY_TICK, EMPTY_TICK];
  }

  return {
    metar_station: nearestMsc ? nearestMsc.s.station_name : null,
    metar_distance_km: nearestMsc ? Math.round(nearestMsc.d) : null,
    forecast,
    sensors,
  };
}

// Unified data hook: same return shape in both modes.
function useWeatherData(lat: number, lng: number): { data: WxResponse | null; isPending: boolean } {
  const allStations = useWxStore((s) => s.allStations);
  const liveData = useMemo(
    () => (config.dataSource === 'live' ? buildLiveResponse(lat, lng, allStations) : null),
    [lat, lng, allStations],
  );
  const rest = useQuery<WxResponse>({
    queryKey: ['weather', lat.toFixed(2), lng.toFixed(2)],
    queryFn: () =>
      http
        .get('api/weather', { searchParams: { lat: String(lat), lng: String(lng) } })
        .json<WxResponse>(),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: config.dataSource === 'mock',
  });
  if (config.dataSource === 'live') {
    return { data: liveData, isPending: liveData == null };
  }
  return { data: rest.data ?? null, isPending: rest.isPending };
}

// Formatting helpers ─ keep "—" semantics consistent across cells.
const fmtNum = (v: number | null | undefined, digits = 0, suffix = '') =>
  v == null ? '—' : `${v.toFixed(digits)}${suffix}`;
const fmtStr = (v: string | null | undefined) => (v == null || v === '' ? '—' : v);

// ENG-271: forecast, feels-like, visibility, conditions and gusts are hidden for
// now (no data behind them); flags live in lib/droneContextFlags.ts.
const show = droneContextFlags.weather;

export function WeatherTab({ lat, lng }: { lat: number; lng: number }) {
  const [idx, setIdx] = useState(0);
  const { data, isPending } = useWeatherData(lat, lng);
  const showWx = useUiStore((s) => s.showWxSensors);
  const updatedAt = useWxStore((s) => s.allStationsAt);
  const toggle = useUiStore((s) => s.toggle);
  const setSensors = useWxStore((s) => s.setSensors);
  const clearSensors = useWxStore((s) => s.clear);

  // Push every nearby station into the wx store. The pin layer + popup
  // are both null-tolerant (popup omits wind/temp lines when null), so
  // pins should always match the list — previously we dropped any
  // station missing wind, which made "Show on Map" render far fewer
  // pins than the list showed.
  useEffect(() => {
    if (!data) return;
    const out: WxSensorPoint[] = data.sensors.map((s) => {
      const br = (s.bearingDeg * Math.PI) / 180;
      const dLat = (s.distKm * Math.cos(br)) / 111.32;
      const dLng = (s.distKm * Math.sin(br)) / (111.32 * Math.cos((lat * Math.PI) / 180));
      return {
        station_name: s.station_name,
        source: s.source,
        lat: lat + dLat,
        lng: lng + dLng,
        tempC: s.tempC,
        windSpdMph: s.windSpdMph,
        windDirDeg: s.windDirDeg,
        // Carried for the map popup body (mockup `toggleWxSensorMap` —
        // Location · source subtitle + Humidity + Pressure rows +
        // optional Camera Feed link).
        humidityPct: s.humidityPct,
        pressureHpa: s.pressureHpa,
        location: s.location,
        cameraUrl: s.camera_url,
      };
    });
    setSensors(out);
  }, [data, lat, lng, setSensors]);

  // Clear the map pins and per-station toggles only when the tab goes away. This
  // used to run on every data refresh, which reset the toggles a moment after
  // they were pressed, so "Map" appeared not to work.
  useEffect(() => () => clearSensors(), [clearSensors]);

  if (isPending) {
    return (
      <div className="space-y-2">
        <div className="text-[0.769em] text-(--color-text2)">
          Waiting for weather from the broker. It publishes about once a minute, so this fills
          in within a minute at most, and instantly on later visits.
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  if (!data) return <div className="text-[0.769em] text-(--color-text3)">Weather unavailable.</div>;
  const fc = data.forecast[idx]!;

  // Turning the pins on also zooms out to take in the stations: they are 10 to 50 km
  // from the drone, so on the map's usual zoom they are all off screen.
  const onToggleAll = () => {
    const turningOn = !showWx;
    toggle('showWxSensors');
    if (turningOn) fitMapToPoints([{ lat, lng }, ...useWxStore.getState().sensors]);
  };

  // Live only: how old the station data is (it can be a restored snapshot).
  const ageMin = updatedAt != null ? Math.floor((Date.now() - updatedAt) / 60_000) : null;

  return (
    <SectionGroup>
      {config.dataSource === 'live' && ageMin != null && ageMin >= 2 && (
        <div className="text-[0.692em] text-(--color-text3) font-mono">
          Station data from {ageMin < 60 ? `${ageMin} min` : `${Math.floor(ageMin / 60)} h`} ago, updating when the next batch arrives.
        </div>
      )}
      {show.forecast && (
        <div className="rounded-md border border-(--color-border) bg-(--color-bg) p-2.5">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">Forecast Time</span>
            <span className="text-[0.769em] font-bold font-mono text-(--color-green)">
              {idx === 0 ? 'Now' : `+${idx}h`}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={4}
            step={1}
            value={idx}
            onChange={(e) => setIdx(Number(e.target.value))}
            className="w-full accent-(--color-green)"
          />
          <div className="flex justify-between text-[0.615em] font-mono text-(--color-text3) mt-0.5">
            <span>Now</span>
            <span>+1h</span>
            <span>+2h</span>
            <span>+3h</span>
            <span>+4h</span>
          </div>
        </div>
      )}

      <Section title="Conditions">
        <MetricRow3>
          {show.temperature && (
            <MetricCell
              label="Temperature"
              value={fmtNum(fc.tempC, 0, '°C')}
              sub={
                show.feelsLike
                  ? fc.feelsC != null
                    ? `Feels ${fc.feelsC.toFixed(0)}°C`
                    : 'Feels —'
                  : null
              }
            />
          )}
          <MetricCell
            label="Humidity"
            value={fmtNum(fc.humidityPct, 0, '%')}
            sub={fmtNum(fc.pressureHpa, 0, ' hPa')}
          />
          {show.visibility && (
            <MetricCell
              label="Visibility"
              value={fmtNum(fc.visKm, 0, ' km')}
              tone={fc.visKm == null ? undefined : fc.visKm >= 10 ? 'ok' : fc.visKm >= 5 ? 'warn' : 'crit'}
              sub={fc.cloudsPct != null ? `${fc.cloudsPct}% cloud` : '— cloud'}
            />
          )}
        </MetricRow3>
        <div className="rounded-md border border-(--color-border) bg-(--color-surface) px-3 py-1 mt-1.5">
          {show.conditions && <InfoRow label="Conditions" value={fmtStr(fc.description)} mono />}
          <InfoRow
            label="METAR Stn"
            value={
              data.metar_station
                ? `${data.metar_station}${data.metar_distance_km != null ? ` (${data.metar_distance_km} km)` : ''}`
                : '—'
            }
            mono
          />
        </div>
      </Section>

      <Section title="Wind">
        <MetricRow3>
          <MetricCell label="Speed" value={fmtNum(fc.windSpdKts, 0, ' kts')} />
          <MetricCell
            label="Direction"
            value={fmtNum(fc.windDirDeg, 0, '°')}
            sub={fc.windDirDeg != null ? degToCardinal(fc.windDirDeg) : '—'}
          />
          {show.gusts && (
            <MetricCell
              label="Gusts"
              value={fmtNum(fc.windGustKts, 0, ' kts')}
              tone={fc.windGustKts == null ? undefined : fc.windGustKts > 35 ? 'crit' : fc.windGustKts > 20 ? 'warn' : 'ok'}
            />
          )}
        </MetricRow3>
      </Section>

      <Section
        title="Weather Sensors"
        action={
          <Button size="xs" variant={showWx ? 'default' : 'outline'} onClick={onToggleAll}>
            {/* Mockup `wx-sens-map-btn` L3095-L3098 uses ◇ (U+25C7) when off and
                ◆ (U+25C6) when on — matches the diamond pin on the map. */}
            <span className="text-[0.692em] leading-none">{showWx ? '◆' : '◇'}</span>{' '}
            {showWx ? 'On Map' : 'Show on Map'}
          </Button>
        }
      >
        <p className="text-[0.692em] text-(--color-text3) -mt-1 mb-1.5 leading-snug">
          {data.sensors.length} station{data.sensors.length === 1 ? '' : 's'} within {NEARBY_RADIUS_KM} km of the drone.
        </p>
        <div className="space-y-1.5">
          {data.sensors.length === 0 && (
            <div className="text-[0.769em] text-(--color-text3) text-center py-4">No sensors within range.</div>
          )}
          {data.sensors.map((s) => (
            <SensorRow key={s.station_name} s={s} droneLat={lat} droneLng={lng} />
          ))}
        </div>
      </Section>
    </SectionGroup>
  );
}

function SensorRow({ s, droneLat, droneLng }: { s: WxStation; droneLat: number; droneLng: number }) {
  const sourceTone = s.source === 'Alberta511' ? 'info' : s.source === 'Netatmo' ? 'solid' : 'muted';
  const on = useWxStore((st) => st.perSensorOn[s.station_name] ?? false);
  const toggleSensor = useWxStore((st) => st.toggleSensor);
  const showStation = () => {
    const turningOn = !on;
    toggleSensor(s.station_name);
    if (!turningOn) return;
    const pin = useWxStore.getState().sensors.find((p) => p.station_name === s.station_name);
    if (pin) fitMapToPoints([{ lat: droneLat, lng: droneLng }, pin]);
  };
  return (
    <Accordion
      title={
        <span className="flex items-center gap-1.5">
          <span>{s.station_name}</span>
          <Badge variant={sourceTone}>{s.source}</Badge>
          {/* A <span role="button">, not a <button>: this sits inside the accordion's
              own header button, and nested buttons are invalid HTML (React logs an error). */}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              showStation();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                showStation();
              }
            }}
            aria-pressed={on}
            className={cn(
              'ml-auto inline-flex items-center h-5 px-1.5 rounded-full text-[0.615em] font-semibold border transition-colors cursor-pointer',
              on
                ? 'bg-(--color-green) text-(--color-on-accent) border-(--color-green)'
                : 'bg-(--color-surface) text-(--color-text3) border-(--color-border) hover:border-(--color-green)',
            )}
            title="Show this sensor on the map"
          >
            <MapPin size={10} className="inline -mt-px" /> Map
          </span>
        </span>
      }
      meta={
        <span className="font-mono">
          {s.distKm.toFixed(1)} km · {fmtNum(s.windSpdMph, 0, ' mph')} · {fmtNum(s.tempC, 0, '°')}
        </span>
      }
    >
      <div className="grid grid-cols-2 gap-1">
        <InfoRow label="Wind Speed" value={fmtNum(s.windSpdMph, 1, ' mph')} mono />
        <InfoRow
          label="Direction"
          value={s.windDirDeg != null ? `${Math.round(s.windDirDeg)}° ${degToCardinal(s.windDirDeg)}` : '—'}
          mono
        />
        <InfoRow label="Temperature" value={fmtNum(s.tempC, 1, '°C')} mono />
        <InfoRow label="Humidity" value={fmtNum(s.humidityPct, 0, '%')} mono />
        <InfoRow label="Pressure" value={fmtNum(s.pressureHpa, 0, ' hPa')} mono />
        <InfoRow label="Dew Point" value={fmtNum(s.dewPointC, 1, '°C')} mono />
      </div>
      <InfoRow label="Location" value={fmtStr(s.location)} />
      {s.camera_url && (
        <a
          href={s.camera_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[0.692em] text-(--color-green) font-semibold mt-1 inline-block hover:underline"
        >
          View Camera Feed →
        </a>
      )}
    </Accordion>
  );
}
